import { useDocuments, useRoadmap, useTasks } from '@/shared/api/queries'
import { useLocalization, useTranslate } from '@/shared/i18n'
import {
  areaIcon,
  areaLabel,
  areaOf,
  evaluationsOf,
  groupByMonth,
  timelineOf,
} from '@/shared/model'
import { Row, Section, Skeleton, Stack, StateBlock, Text } from '@/shared/ui'

import { taskRoute } from '@/app/routes'

import { TimelineEntries } from './TimelineEntries'

export function TimelinePage() {
  const t = useTranslate()
  const { language } = useLocalization()

  const tasks = useTasks()
  const documents = useDocuments()
  const roadmap = useRoadmap()

  const evaluations = evaluationsOf(roadmap.data)
  const months = groupByMonth(timelineOf(tasks.data ?? [], documents.data ?? []))
  const undated = (tasks.data ?? []).filter((task) => task.status === 'open' && !task.deadline)

  return (
    <Stack gap="section">
      <Text as="h1" role="heading-2">
        {t('timeline.title')}
      </Text>

      <StateBlock
        pending={tasks.isPending || documents.isPending}
        error={tasks.error ?? documents.error}
        onRetry={() => void (tasks.error ? tasks.refetch() : documents.refetch())}
        skeleton={<Skeleton variant="row" rows={5} />}
        {...(months.length === 0 && undated.length === 0
          ? { empty: { title: t('timeline.empty.title'), body: t('timeline.empty.body') } }
          : {})}
      >
        <Stack gap="section">
          {months.length > 0 ? (
            <TimelineEntries months={months} evaluations={evaluations} language={language} />
          ) : null}

          {undated.length > 0 ? (
            <Section title={t('timeline.undated')}>
              <Stack as="ul" gap="sm">
                {undated.map((task) => (
                  <Row
                    key={task.id}
                    to={taskRoute(task.id)}
                    title={`${areaIcon(areaOf(task, evaluations) ?? 'other')} ${task.title}`}
                    meta={t(areaLabel(areaOf(task, evaluations) ?? 'other'))}
                  />
                ))}
              </Stack>
            </Section>
          ) : null}
        </Stack>
      </StateBlock>
    </Stack>
  )
}
